import { useState } from 'react'
import {
  Badge,
  Card,
  CardBody,
  Col,
  Nav,
  NavItem,
  NavLink,
  Row,
  Button,
} from 'reactstrap'
import classnames from 'classnames'
import { MapPin } from 'react-feather'
import { formatDateOnlyPMB } from 'utils/formatDate'
import { newFormatThousandSeparator } from 'utils/formatNumber'
import { useFetchGenericDataInsuranceQuery } from './detailFarmersApiSlice'
import ModalInsuranceMap from './ModalInsuranceMap'

const KNOWN_FIELDS = new Set([
  'nik',
  'desa',
  'npwp',
  'skip',
  'batch',
  'sheet',
  'tahun',
  'row_no',
  'komoditi',
  'latitude',
  'provinsi',
  'varietas',
  'kabupaten',
  'kecamatan',
  'longitude',
  'alamat_ktp',
  'group_desa',
  'skipReason',
  'ump_lokasi',
  'jarak_tanam',
  'musim_tanam',
  'source_file',
  'coord_status',
  'latitude_raw',
  'nama_peserta',
  'jenis_kelamin',
  'longitude_raw',
  'luas_lahan_ha',
  'risiko_standar',
  'poktan_gapoktan',
  'premi_per_musim',
  'group_koordinator',
  'risiko_perluasan_1',
  'risiko_perluasan_2',
  'alamat_lokasi_lahan',
  'harga_pertanggungan',
  'mulai_cover_asuransi',
  'sumber_modal_pinjaman',
  'rata_produktivitas_ton',
  'tanggal_mulai_asuransi',
  'sumber_modal_uang_petani',
  'tanggal_berakhir_asuransi',
  'createdAt',
  'updatedAt',
  'deletedAt',
  '__v',
])

const IGNORED_EXTRA_KEYS = new Set([
  'skip',
  'skipReason',
  'coord_status',
  'latitude_raw',
  'longitude_raw',
  'row_no',
  'mulai_cover_asuransi',
  'createdAt',
  'updatedAt',
  'deletedAt',
  '__v',
])

const formatGenericLabel = (key) => {
  if (!key) return ''
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

const formatGenericValue = (val) => {
  if (val === null || val === undefined || val === '') return '-'
  if (typeof val === 'boolean') return val ? 'Ya' : 'Tidak'
  if (typeof val === 'object') return JSON.stringify(val)
  return String(val)
}

const formatCurrency = (val) => {
  if (val === null || val === undefined || val === '' || val === '-') return '-'
  const num = Number(val)
  if (isNaN(num)) return String(val)
  return `Rp ${newFormatThousandSeparator(num, 0)}`
}

const renderRiskBadge = (text) => {
  if (!text || text === '-') {
    return <p className="text-muted mb-0">-</p>
  }
  const lower = String(text).toLowerCase()
  if (lower.includes('dijamin')) {
    return (
      <Badge color="light-success" className="text-success fw-semibold">
        {text}
      </Badge>
    )
  }
  return (
    <Badge color="light-warning" className="text-dark fw-semibold">
      {text}
    </Badge>
  )
}

// Komponen reusable untuk setiap kolom detail (mengikuti gaya Data Diri pada detail petani)
const InsuranceField = ({ label, value, subLabel, isMonospace, children }) => (
  <div>
    <span className="fw-semibold">
      {label}
      {subLabel && (
        <span className="text-muted fw-normal ms-1 small">({subLabel})</span>
      )}
    </span>
    {children ? (
      <div className="mt-1">{children}</div>
    ) : (
      <p className={`text-muted mb-0 ${isMonospace ? 'font-monospace' : ''}`}>
        {value !== null && value !== undefined && value !== '' ? value : '-'}
      </p>
    )}
  </div>
)

const InsuranceDataCard = ({ userId }) => {
  const [activeTab, setActiveTab] = useState(0)
  const [isMapModalOpen, setIsMapModalOpen] = useState(false)
  const [selectedMapData, setSelectedMapData] = useState(null)

  const toggleMapModal = () => setIsMapModalOpen((prev) => !prev)

  const handleOpenMap = (item) => {
    setSelectedMapData(item)
    setIsMapModalOpen(true)
  }

  const { data, isLoading, isError } = useFetchGenericDataInsuranceQuery(userId, {
    skip: !userId,
  })

  // Sembunyikan seluruh island jika sedang loading, error, atau tidak ada response
  if (isLoading || isError || !data) {
    return null
  }

  const responseObj =
    data?.data !== undefined && data?.found !== undefined
      ? data
      : data?.data || data

  // Sembunyikan jika found bernilai false
  if (data?.found === false || responseObj?.found === false) {
    return null
  }

  const rawData = responseObj?.data || responseObj

  // Sembunyikan jika data kosong
  if (!rawData) {
    return null
  }
  if (Array.isArray(rawData) && rawData.length === 0) {
    return null
  }
  if (
    typeof rawData === 'object' &&
    !Array.isArray(rawData) &&
    Object.keys(rawData).length === 0
  ) {
    return null
  }

  // Render detail kartu untuk item musim tanam tertentu
  const renderItemDetails = (item) => {
    if (!item || typeof item !== 'object') {
      return <p className="text-muted mb-0">{formatGenericValue(item)}</p>
    }

    const extraFields = Object.entries(item).filter(
      ([key, val]) =>
        !KNOWN_FIELDS.has(key) &&
        !IGNORED_EXTRA_KEYS.has(key) &&
        val !== '' &&
        val !== null &&
        val !== undefined,
    )

    const lat = item.latitude ? parseFloat(item.latitude) : null
    const lng = item.longitude ? parseFloat(item.longitude) : null
    const hasValidCoords =
      lat !== null && lng !== null && !isNaN(lat) && !isNaN(lng)

    return (
      <Row className="g-4">
        {/* KOLOM KIRI */}
        <Col lg="6" md="12" className="d-flex flex-column gap-3">
          {/* 1. DATA PESERTA */}
          <Card className="border shadow-none mb-0">
            <CardBody className="d-flex flex-column gap-2 p-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="fw-bold mb-0">Data Peserta</h6>
                {item.row_no && (
                  <Badge color="light-secondary" className="text-muted">
                    No: {item.row_no}
                  </Badge>
                )}
              </div>
              <Row className="g-3">
                <Col xs="6">
                  <InsuranceField label="Nama Peserta" value={item.nama_peserta} />
                </Col>
                <Col xs="6">
                  <InsuranceField
                    label="Jenis Kelamin (L/P)"
                    value={
                      item.jenis_kelamin === 'L'
                        ? 'L (Laki-laki)'
                        : item.jenis_kelamin === 'P'
                          ? 'P (Perempuan)'
                          : item.jenis_kelamin
                    }
                  />
                </Col>
                <Col xs="6">
                  <InsuranceField
                    label="Nomor KTP (NIK)"
                    value={item.nik}
                    isMonospace
                  />
                </Col>
                <Col xs="6">
                  <InsuranceField
                    label="Nomor NPWP"
                    value={item.npwp}
                    isMonospace
                  />
                </Col>
                <Col xs="12">
                  <InsuranceField
                    label="Alamat (sesuai KTP)"
                    value={item.alamat_ktp}
                  />
                </Col>
                <Col xs="12">
                  <InsuranceField
                    label="Poktan/Gapoktan"
                    value={item.poktan_gapoktan}
                  />
                </Col>
                {item.group_koordinator && (
                  <Col xs="6">
                    <InsuranceField
                      label="Group Koordinator"
                      value={item.group_koordinator}
                    />
                  </Col>
                )}
                {item.group_desa && (
                  <Col xs="6">
                    <InsuranceField
                      label="Group Desa"
                      value={item.group_desa}
                    />
                  </Col>
                )}
              </Row>
            </CardBody>
          </Card>

          {/* 2. TANAMAN & BUDIDAYA */}
          <Card className="border shadow-none mb-0">
            <CardBody className="d-flex flex-column gap-2 p-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="fw-bold mb-0">Tanaman & Budidaya</h6>
                {item.musim_tanam && (
                  <Badge color="light-primary" className="text-primary">
                    {item.musim_tanam} {item.tahun ? `(${item.tahun})` : ''}
                  </Badge>
                )}
              </div>
              <Row className="g-3">
                <Col xs="6">
                  <InsuranceField label="Jenis barang" value={item.komoditi} />
                </Col>
                <Col xs="6">
                  <InsuranceField label="Varietas" value={item.varietas} />
                </Col>
                <Col xs="6">
                  <InsuranceField
                    label="Luas Lahan (ha)"
                    subLabel="1 ha = 10.000 m²"
                    value={item.luas_lahan_ha ? `${item.luas_lahan_ha} ha` : '-'}
                  />
                </Col>
                <Col xs="6">
                  <InsuranceField
                    label="Jarak Tanam (cm/benih)"
                    value={item.jarak_tanam}
                  />
                </Col>
                <Col xs="12">
                  <InsuranceField
                    label="Rata-rata Produktivitas (Ton)"
                    subLabel="1 ton = 1.000 kg"
                    value={
                      item.rata_produktivitas_ton
                        ? `${item.rata_produktivitas_ton} Ton`
                        : '-'
                    }
                  />
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* 3. LOKASI LAHAN */}
          <Card className="border shadow-none mb-0">
            <CardBody className="d-flex flex-column gap-2 p-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="fw-bold mb-0">Lokasi Lahan</h6>
                {hasValidCoords && (
                  <Button
                    outline
                    color="primary"
                    size="sm"
                    className="d-inline-flex align-items-center gap-1 py-0 px-2"
                    onClick={() => handleOpenMap(item)}
                  >
                    <MapPin size={13} /> Buka Peta
                  </Button>
                )}
              </div>
              <Row className="g-3">
                <Col xs="12">
                  <InsuranceField
                    label="Alamat Lokasi Lahan"
                    value={item.alamat_lokasi_lahan}
                  />
                </Col>
                <Col xs="6">
                  <InsuranceField label="Desa" value={item.desa} />
                </Col>
                <Col xs="6">
                  <InsuranceField label="Kecamatan" value={item.kecamatan} />
                </Col>
                <Col xs="6">
                  <InsuranceField label="Kabupaten" value={item.kabupaten} />
                </Col>
                <Col xs="6">
                  <InsuranceField label="Provinsi" value={item.provinsi} />
                </Col>
                <Col xs="6">
                  <InsuranceField
                    label="Longitude"
                    value={item.longitude}
                    isMonospace
                  />
                </Col>
                <Col xs="6">
                  <InsuranceField
                    label="Latitude"
                    value={item.latitude}
                    isMonospace
                  />
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>

        {/* KOLOM KANAN */}
        <Col lg="6" md="12" className="d-flex flex-column gap-3">
          {/* 4. SUMBER MODAL USAHA & ASURANSI */}
          <Card className="border shadow-none mb-0">
            <CardBody className="d-flex flex-column gap-2 p-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="fw-bold mb-0">Sumber Modal Usaha & Asuransi</h6>
              </div>
              <Row className="g-3">
                <Col xs="6">
                  <InsuranceField
                    label="Harga Pertanggungan ( Modal Usaha )"
                    value={formatCurrency(item.harga_pertanggungan)}
                  />
                </Col>
                <Col xs="6">
                  <InsuranceField
                    label="Premi Asuransi per musim tanam"
                    value={formatCurrency(item.premi_per_musim)}
                  />
                </Col>
                <Col xs="6">
                  <InsuranceField
                    label="Tanggal Tanam = Mulai Berlaku Asuransi"
                    value={
                      item.tanggal_mulai_asuransi
                        ? formatDateOnlyPMB(item.tanggal_mulai_asuransi)
                        : '-'
                    }
                  />
                </Col>
                <Col xs="6">
                  <InsuranceField
                    label="Tanggal Berakhir Asuransi"
                    value={
                      item.tanggal_berakhir_asuransi
                        ? formatDateOnlyPMB(item.tanggal_berakhir_asuransi)
                        : '-'
                    }
                  />
                </Col>
                <Col xs="6">
                  <InsuranceField
                    label="Pinjaman dari:"
                    value={formatCurrency(item.sumber_modal_pinjaman)}
                  />
                </Col>
                <Col xs="6">
                  <InsuranceField
                    label="Uang petani"
                    value={formatCurrency(item.sumber_modal_uang_petani)}
                  />
                </Col>
                {item.ump_lokasi && (
                  <Col xs="12">
                    <InsuranceField
                      label="UMP Lokasi"
                      value={formatCurrency(item.ump_lokasi)}
                    />
                  </Col>
                )}
              </Row>
            </CardBody>
          </Card>

          {/* 5. DIJAMIN ATAU TIDAK DIJAMIN */}
          <Card className="border shadow-none mb-0">
            <CardBody className="d-flex flex-column gap-2 p-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div>
                  <h6 className="fw-bold mb-0">Dijamin atau Tidak Dijamin</h6>
                </div>
              </div>
              <Row className="g-3">
                <Col xs="12">
                  <InsuranceField label="Risiko Standar: angin puting beliung">
                    {renderRiskBadge(item.risiko_standar)}
                  </InsuranceField>
                </Col>
                <Col xs="12">
                  <InsuranceField label="Risiko Perluasan 1: Semua hama dan penyakit">
                    {renderRiskBadge(item.risiko_perluasan_1)}
                  </InsuranceField>
                </Col>
                <Col xs="12">
                  <InsuranceField label="Risiko Perluasan 2: bencana alam yang dijamin">
                    {renderRiskBadge(item.risiko_perluasan_2)}
                  </InsuranceField>
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* DATA TAMBAHAN (JIKA ADA KEY LAIN) */}
          {extraFields.length > 0 && (
            <Card className="border shadow-none mb-0">
              <CardBody className="d-flex flex-column gap-2 p-3">
                <h6 className="fw-bold mb-2">Data Tambahan</h6>
                <Row className="g-3">
                  {extraFields.map(([k, v]) => (
                    <Col xs="6" key={k}>
                      <InsuranceField
                        label={formatGenericLabel(k)}
                        value={formatGenericValue(v)}
                      />
                    </Col>
                  ))}
                </Row>
              </CardBody>
            </Card>
          )}
        </Col>
      </Row>
    )
  }

  // Pilih render view berdasarkan tipe data
  const renderContent = () => {
    if (Array.isArray(rawData)) {
      const activeItem = rawData[activeTab] || rawData[0]

      return (
        <div>
          {/* Tabs jika ada beberapa musim tanam */}
          {rawData.length > 1 && (
            <Nav tabs className="mb-3">
              {rawData.map((item, idx) => {
                const label = item.musim_tanam || `Musim #${idx + 1}`
                const year = item.tahun ? ` ${item.tahun}` : ''
                const commodity = item.komoditi ? ` (${item.komoditi})` : ''
                return (
                  <NavItem key={idx}>
                    <NavLink
                      className={classnames({
                        active: activeTab === idx,
                        'fw-semibold': activeTab === idx,
                      })}
                      onClick={() => setActiveTab(idx)}
                      style={{ cursor: 'pointer' }}
                    >
                      {label}
                      {year}
                      {commodity}
                    </NavLink>
                  </NavItem>
                )
              })}
            </Nav>
          )}

          {renderItemDetails(activeItem)}
        </div>
      )
    }

    if (typeof rawData === 'object') {
      return renderItemDetails(rawData)
    }

    return (
      <div>
        <p className="text-muted mb-0">{formatGenericValue(rawData)}</p>
      </div>
    )
  }

  return (
    <>
      <Card className="border-0 shadow-sm" data-testid="farmer-insurance-card">
        <CardBody className="d-flex flex-column gap-3">
          <div className="d-flex align-items-center gap-2">
            <h5 className="mb-0">Data Insurance</h5>
            {Array.isArray(rawData) && rawData.length > 0 && (
              <span className="text-muted small ms-1">
                ({rawData.length} Musim Tanam)
              </span>
            )}
          </div>

          {renderContent()}
        </CardBody>
      </Card>

      {/* Modal Peta Lokasi Lahan */}
      <ModalInsuranceMap
        isOpen={isMapModalOpen}
        toggle={toggleMapModal}
        data={selectedMapData}
      />
    </>
  )
}

export default InsuranceDataCard
